import { Breadcrumb, Button, Col, Form, Input, Row, Select } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import editProfile from "../../assets/images/icon/editProfile.svg";
import SuccessIcon from "../../assets/images/icon/successIcon.svg";
import Heading from "../../components/common/heading";
import Previous from "../../components/common/previous";
import { ERR_0051, USER_DATA } from "../../lib/const";
import useAuth from "../../lib/hooks/useAuth";
import useCancelModal from "../../lib/hooks/useCancelModal";
import useEState from "../../lib/hooks/useEState";
import useLineOfWork from "../../lib/hooks/useLineOfWork";
import { routes } from "../../routes";
import { getProfile, updateProfile } from "../../services/profileService";
import WithSidebar from "../../templates/withSidebar";

const Option = Select.Option;

const Profile = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const { auth, setAuth } = useAuth();
  const { setOpenCancelModal } = useCancelModal();
  const [estates] = useEState();
  const [lineOfWork] = useLineOfWork();
  const [submittable, setSubmittable] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const setProfileValues = useCallback(() => {
    let text = auth?.user?.fullName;
    const full_n = text?.split(" ");
    let first_n,
      last_n = "";
    if (full_n) {
      first_n = full_n[0];
      last_n = full_n[1];
    }

    form.setFieldsValue({
      firstName: first_n,
      lastName: last_n,
      email: auth?.user?.email,
      estateId: auth?.user?.estateId,
      lineOfWorkId: auth?.user?.lineOfWorkId,
    });
  }, [form, auth]);

  useEffect(() => {
    setProfileValues();
  }, [setProfileValues]);

  const onFinish = async (values) => {
    const request = {
      userId: auth?.user?.userId,
      username: auth?.user?.username,
      firstname: values.firstName,
      lastname: values.lastName,
      roleId: auth?.user?.roleId,
      email: values.email,
      description: auth?.user?.description,
      estateId: values.estateId,
      lineOfWorkId: values.lineOfWorkId,
      fromPage: "profile",
    };

    const res = await updateProfile(request);
    if (res.data.code === "0000") {
      const resp = await getProfile();
      if (resp.data.code === "0000") {
        const { objectValue } = resp.data;
        setAuth({
          user: objectValue,
          isLogin: true,
        });
        localStorage.setItem(USER_DATA, JSON.stringify(objectValue));
      }
      setUpdateStatus(true);
    } else {
      setUpdateStatus(false);
      if (res?.data?.code === "ERR-9999") {
        setErrMsg("เกิดข้อผิดพลาดภายในเซิร์ฟเวอร์");
      } else if (res?.data?.code === ERR_0051) {
        setErrMsg("ระบบไม่สามารถดำเนินการส่งเมลได้");
      } else {
        setErrMsg("ขออภัย, มีเกิดข้อผิดพลาดบางอย่าง");
      }
    }
  };

  useEffect(() => {
    if (
      values?.firstName === "" ||
      values?.lastName === "" ||
      values?.email === "" ||
      !values?.estateId ||
      !values?.lineOfWorkId
    ) {
      setSubmittable(true);
    } else {
      setErrMsg("");
      setSubmittable(false);
    }
  }, [values, form, submittable]);

  return (
    <WithSidebar>
      <Row gutter={[16]}>
        <Col span={24}>
          <Previous text={"แก้ไขข้อมูลส่วนตัว"} prevLink={routes.dashboard} />
          <Breadcrumb
            style={{ paddingTop: 10 }}
            items={[
              {
                title: "ข้อมูลส่วนตัว",
              },
              {
                title: <span className="pr_green">แก้ไขข้อมูลส่วนตัว</span>,
              },
            ]}
          />
        </Col>
      </Row>

      <div style={{ margin: "40px 200px" }}>
        {!updateStatus ? (
          <>
            <Row gutter={[24]} style={{ marginBottom: 25 }}>
              <Col span={24} align="center">
                <img
                  src={editProfile}
                  width={100}
                  height={100}
                  alt="edit profile"
                />
              </Col>
              <Col span={24} align="center">
                <Heading
                  text={"แก้ไขข้อมูลส่วนตัว"}
                  size={4}
                  style={{ margin: 0 }}
                />
                <p style={{ color: "#8187A6" }}>
                  ระบุข้อมูลส่วนตัวที่ต้องการจะแก้ไขได้เลย
                </p>

                {errMsg && (
                  <p style={{ color: "#F04438", marginTop: 20 }}>{errMsg}</p>
                )}
              </Col>
            </Row>
            <Form
              form={form}
              name="validateOnly"
              layout="vertical"
              onFinish={onFinish}
              autoComplete="off"
            >
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <Form.Item
                    label="ชื่อจริง"
                    name={"firstName"}
                    rules={[
                      {
                        required: true,
                        message: "กรุณาระบุชื่อจริง",
                      },
                    ]}
                  >
                    <Input placeholder="ชื่อจริง" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="นามสกุล"
                    name={"lastName"}
                    rules={[
                      {
                        required: true,
                        message: "กรุณาระบุนามสกุล",
                      },
                    ]}
                  >
                    <Input placeholder="นามสกุล" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label="อีเมล"
                    name={"email"}
                    rules={[
                      {
                        type: "email",
                        required: true,
                        message: "กรุณาระบุอีเมล",
                      },
                    ]}
                  >
                    <Input placeholder="Ex.abc@gmail.com..." />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="ชื่อนิคม"
                    name={"estateId"}
                    rules={[
                      {
                        required: true,
                        message: "กรุณาระบุชื่อนิคม",
                      },
                    ]}
                  >
                    <Select
                      allowClear
                      style={{
                        width: "100%",
                      }}
                      placeholder="กรุณาเลือก"
                    >
                      {estates.map((item) => (
                        <Option value={item.estateId}>
                          {item.estateNameTh}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="สายปฏิบัติงาน"
                    name={"lineOfWorkId"}
                    rules={[
                      {
                        required: true,
                        message: "กรุณาระบุสายปฏิบัติงาน",
                      },
                    ]}
                  >
                    <Select
                      allowClear
                      style={{
                        width: "100%",
                      }}
                      placeholder="กรุณาเลือก"
                    >
                      {lineOfWork.map((item) => (
                        <Option value={item.lineOfWork}>{item.nameTh}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={24} align="center" style={{ marginTop: 20 }}>
                  <Button
                    style={{ marginRight: 10 }}
                    onClick={() => {
                      setOpenCancelModal(true);
                    }}
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    disabled={submittable}
                  >
                    ส่งข้อมูล
                  </Button>
                </Col>
              </Row>
            </Form>
          </>
        ) : (
          <Row gutter={[24]} style={{ marginTop: 100 }}>
            <Col span={24} align="center">
              <img
                src={SuccessIcon}
                width={100}
                height={100}
                alt="edit profile"
              />
            </Col>
            <Col span={24} align="center">
              <Heading
                text={"ส่งข้อมูลสำเร็จ"}
                size={4}
                style={{ margin: 0 }}
              />
              <p style={{ color: "#8187A6" }}>
                ท่านได้ทำการส่งข้อมูลส่วนตัวที่ต้องการแก้ไขให้ Admin แล้ว <br />
                ทางทีมงานของเราจะดำเนินการแก้ไขให้โดยเร็วที่สุด
              </p>
            </Col>

            <Col span={24} align="center">
              <Button
                type="primary"
                onClick={() => {
                  navigate(routes.dashboard);
                }}
              >
                กลับหนัาหลัก
              </Button>
            </Col>
          </Row>
        )}
      </div>
    </WithSidebar>
  );
};

export default Profile;
